import React, { useRef, useEffect } from 'react';
import { PencilIcon, TrashIcon, MoreVertical } from 'lucide-react';

const SessionItem = ({
    session,
    isActive,
    isEditing,
    editingName,
    isMenuOpen,
    onMenuToggle,
    onSelect,
    onStartEdit,
    onRename,
    onDelete,
    onEditNameChange
}) => {
    const menuRef = useRef(null);

    useEffect(() => {
        if (!isMenuOpen) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onMenuToggle(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isMenuOpen, onMenuToggle]);

    const handleSessionClick = () => {
        if (!isEditing) {
            onSelect(session.session_id);
        }
    };

    const handleRename = () => {
        onRename(session.session_id);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleRename();
        } else if (e.key === 'Escape') {
            onRename(session.session_id);
        }
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
        onMenuToggle(!isMenuOpen);
    };

    const handleMenuAction = (action) => (e) => {
        e.stopPropagation();
        action();
        onMenuToggle(false);
    };

    if (isEditing) {
        return (
            <div className="px-2 py-1 rounded-md bg-[#0000000c]">
                <input
                    type="text"
                    className="w-full bg-transparent text-gray-800 font-normal text-sm px-0 py-0 focus:outline-none"
                    value={editingName}
                    onChange={(e) => onEditNameChange(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                />
            </div>
        );
    }

    return (
        <div
            className={`group flex items-center gap-2 px-2 py-1 rounded-md text-sm relative ${
                isActive ? 'bg-[#00000015]' : 'hover:bg-[#0000000c]'
            }`}
        >
            <div
                className="flex-1 truncate text-gray-800 font-normal cursor-pointer min-w-0"
                onClick={handleSessionClick}
                title={session.session_name || "New Chat"}
            >
                {session.session_name || "New Chat"}
            </div>

            <button
                onClick={handleMenuClick}
                className="flex-shrink-0 overflow-hidden p-1 -mr-2 text-gray-500 hover:text-black rounded hover:bg-[#0000000c] w-auto opacity-100 sm:w-0 sm:opacity-0 sm:group-hover:w-auto sm:group-hover:opacity-100 transition-all duration-200"
                aria-label="Session options"
            >
                <MoreVertical className="w-4 h-4" />
            </button>

            {isMenuOpen && (
                <div
                    ref={menuRef}
                    className="absolute right-0 top-full mt-1 w-36 bg-app-bg border border-app-border rounded-md shadow-lg z-10"
                >
                    <button
                        onClick={handleMenuAction(() => onStartEdit(session))}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-[#0000000c] rounded-t-md"
                    >
                        <PencilIcon className="w-3.5 h-3.5" />
                        <span>Đổi tên</span>
                    </button>
                    <button
                        onClick={handleMenuAction(() => onDelete(session.session_id))}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-b-md"
                    >
                        <TrashIcon className="w-3.5 h-3.5" />
                        <span>Xóa</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SessionItem;